/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { FC, useState } from 'react';
import { Empty, message, Tag } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import { useIntl } from 'react-intl';
import IconButton from '~components/_common/IconButton/IconButton';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import {
  getOffersVoucherAttachmentDownloadUrl,
  getVoucherAttachmentDownloadUrl,
} from '~src/services/api/contracts';
import {
  renderOptionalVoucherBadge,
  renderVoucherTypeIcon,
} from '~components/molecules/Tabs/TenderTabs/components/ContractAndOffers/utils';
import { EvaluationMergedVoucher } from './evaluationData';
import Viewer from '~components/atoms/Viewers/Viewer';

type Attachment = {
  id?: string;
  file_guid?: string;
  guid?: string;
  name?: string;
  filename?: string;
  title?: string;
  type?: string;
  serial_number?: number;
};

type AttachmentSource = 'project' | 'vendor';

type OffersEvaluationExpandedProps = {
  projectId?: number;
  tenderId: number | string;
  contractId?: number | null;
  offerId: number | null;
  vouchers: EvaluationMergedVoucher[];
};

export const OffersEvaluationExpanded: FC<OffersEvaluationExpandedProps> = ({
  projectId,
  tenderId,
  contractId,
  offerId,
  vouchers,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  // Preview drawer state
  const [previewDrawerVisible, setPreviewDrawerVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState<{
    url: string;
    name: string;
    extension: string;
    voucher: EvaluationMergedVoucher;
    attachment: Attachment;
    source: AttachmentSource;
  } | null>(null);
  const [isLoadingUrl, setIsLoadingUrl] = useState(false);

  const visibleVouchers = vouchers.filter((voucher) => {
    const voucherItem = voucher as any;
    const voucherType = voucherItem.voucherType ?? voucherItem.voucher_type;

    return (
      voucherItem.is_voucher_group !== true &&
      voucherItem.isVoucherGroup !== true &&
      [1, 2, 3, 4].includes(Number(voucherType))
    );
  });

  const unitTypes = [
    { value: 1, label: intl.formatMessage({ id: 'centimeter' }) },
    { value: 2, label: intl.formatMessage({ id: 'meter' }) },
    { value: 3, label: intl.formatMessage({ id: 'square-meter' }) },
    { value: 4, label: intl.formatMessage({ id: 'cubic-meter' }) },
    { value: 5, label: intl.formatMessage({ id: 'piece' }) },
    { value: 6, label: intl.formatMessage({ id: 'sum' }) },
    { value: 7, label: intl.formatMessage({ id: 'kilogram' }) },
    { value: 8, label: intl.formatMessage({ id: 'ton' }) },
    { value: 9, label: intl.formatMessage({ id: 'timer' }) },
    { value: 10, label: intl.formatMessage({ id: 'day' }) },
    { value: 11, label: intl.formatMessage({ id: 'week' }) },
    { value: 12, label: intl.formatMessage({ id: 'month' }) },
  ];

  const getVoucherType = (voucher: EvaluationMergedVoucher) => {
    return Number(voucher.voucherType);
  };

  const getUnitTypeLabel = (unitType?: number | null) => {
    return unitTypes.find((unit) => unit.value === unitType)?.label || null;
  };

  const getAttachmentGuid = (attachment: Attachment) => {
    return attachment?.id || attachment?.file_guid || attachment?.guid;
  };

  const getAttachmentName = (attachment: Attachment, index: number) => {
    return (
      attachment?.name ||
      attachment?.filename ||
      attachment?.title ||
      intl.formatMessage(
        {
          id: 'evaluation-expanded-file-fallback-name',
        },
        { index: index + 1 }
      )
    );
  };

  const formatFilename = (name: string, maxLength: number) => {
    const lastDot = name.lastIndexOf('.');

    if (lastDot === -1) {
      if (name.length <= maxLength) return name;
      return name.slice(0, maxLength) + '…';
    }

    const base = name.slice(0, lastDot);
    const ext = name.slice(lastDot);

    if (name.length <= maxLength) return name;

    const shortenedBase = base.slice(0, maxLength - ext.length - 1);

    return shortenedBase + '…' + ext;
  };

  const isPreviewable = (attachment: Attachment) => {
    const fileName = getAttachmentName(attachment, 0).toLowerCase();
    const isPdf =
      attachment.type === 'application/pdf' || fileName.endsWith('.pdf');
    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
    return isPdf || isImage;
  };

  const getFileExtension = (attachment: Attachment): string | undefined => {
    const fileName = getAttachmentName(attachment, 0);
    const lastDot = fileName.lastIndexOf('.');
    if (lastDot === -1) return undefined;
    return fileName.slice(lastDot);
  };

  const handleAttachmentPreview = async (
    voucher: EvaluationMergedVoucher,
    attachment: Attachment,
    source: AttachmentSource
  ) => {
    const fileGuid = getAttachmentGuid(attachment);

    if (!projectId || !tenderId || !voucher.id || !fileGuid) {
      message.error(
        intl.formatMessage({
          id: 'missing-contract-data-download',
        })
      );
      return;
    }

    const fileName = getAttachmentName(attachment, 0);
    const extension = getFileExtension(attachment);

    // Open drawer immediately with empty URL (shows loading spinner)
    setPreviewFile({
      url: '',
      name: fileName,
      extension: extension || '',
      voucher,
      attachment,
      source,
    });
    setPreviewDrawerVisible(true);
    setIsLoadingUrl(true);

    try {
      let url: string;

      if (source === 'vendor') {
        if (!offerId) {
          message.error(
            intl.formatMessage({
              id: 'missing-contract-data-download',
            })
          );
          setPreviewDrawerVisible(false);
          setPreviewFile(null);
          return;
        }

        url = await getOffersVoucherAttachmentDownloadUrl({
          projectId,
          tenderId: String(tenderId),
          offerId,
          voucherId: String(voucher.id),
          fileGuid,
        });
      } else {
        if (!contractId) {
          message.error(
            intl.formatMessage({
              id: 'missing-contract-data-download',
            })
          );
          setPreviewDrawerVisible(false);
          setPreviewFile(null);
          return;
        }

        url = await getVoucherAttachmentDownloadUrl({
          projectId,
          tenderId: String(tenderId),
          contractId,
          voucherId: String(voucher.id),
          fileGuid,
        });
      }

      // Update with actual URL - Viewer will re-render with content
      setPreviewFile({
        url,
        name: fileName,
        extension: extension || '',
        voucher,
        attachment,
        source,
      });
    } catch (err) {
      message.error(
        intl.formatMessage({
          id: 'attachment-download-failed',
        })
      );
      setPreviewDrawerVisible(false);
      setPreviewFile(null);
    } finally {
      setIsLoadingUrl(false);
    }
  };

  const handleAttachmentDownload = async (
    voucher: EvaluationMergedVoucher,
    attachment: Attachment,
    source: AttachmentSource
  ) => {
    try {
      const fileGuid = getAttachmentGuid(attachment);

      if (!projectId || !tenderId || !voucher.id || !fileGuid) {
        message.error(
          intl.formatMessage({
            id: 'missing-contract-data-download',
          })
        );
        return;
      }

      let url: string;

      if (source === 'vendor') {
        if (!offerId) {
          message.error(
            intl.formatMessage({
              id: 'missing-contract-data-download',
            })
          );
          return;
        }

        url = await getOffersVoucherAttachmentDownloadUrl({
          projectId,
          tenderId: String(tenderId),
          offerId,
          voucherId: String(voucher.id),
          fileGuid,
        });
      } else {
        if (!contractId) {
          message.error(
            intl.formatMessage({
              id: 'missing-contract-data-download',
            })
          );
          return;
        }

        url = await getVoucherAttachmentDownloadUrl({
          projectId,
          tenderId: String(tenderId),
          contractId,
          voucherId: String(voucher.id),
          fileGuid,
        });
      }

      window.open(url, '_blank');
    } catch (err) {
      message.error(
        intl.formatMessage({
          id: 'attachment-download-failed',
        })
      );
    }
  };

  const renderAttachmentRow = (
    voucher: EvaluationMergedVoucher,
    attachment: Attachment,
    index: number,
    source: AttachmentSource
  ) => {
    const fileName = getAttachmentName(attachment, index);
    const displayName = formatFilename(fileName, 34);
    const normalizedFileName = fileName.toLowerCase();

    const isPdf =
      attachment.type === 'application/pdf' ||
      normalizedFileName.endsWith('.pdf');
    const isXls =
      attachment.type === 'application/xlsx' ||
      attachment.type === 'application/xls' ||
      normalizedFileName.endsWith('.xlsx') ||
      normalizedFileName.endsWith('.xls');

    const canPreview = isPreviewable(attachment);

    return (
      <div
        key={`${getAttachmentGuid(attachment) || fileName}-${index}`}
        css={styles.fileItem}>
        <div css={styles.fileItemLeft}>
          {isPdf ? (
            <IconButton
              icon={'file-pdf'}
              iconStyle={'fad'}
              color={'red'}
              backgroundOff
              customStyle={{ fontSize: '18px' }}
              containerStyle={styles.fileIconContainer as any}
            />
          ) : isXls ? (
            <IconButton
              icon={'file-spreadsheet'}
              iconStyle={'fad'}
              color={'green'}
              backgroundOff
              customStyle={{ fontSize: '18px' }}
              containerStyle={styles.fileIconContainer as any}
            />
          ) : (
            <PaperClipOutlined style={{ fontSize: '18px' }} />
          )}

          <span css={styles.fileName}>{displayName}</span>
        </div>

        <div style={{ display: 'flex', gap: '4px' }}>
          {canPreview && (
            <IconButton
              icon={'eye'}
              iconStyle={'far'}
              color={'red'}
              customStyle={{ fontSize: '16px' }}
              click={() => handleAttachmentPreview(voucher, attachment, source)}
            />
          )}
          <IconButton
            icon={'download'}
            iconStyle={'far'}
            color={'blue'}
            customStyle={{ fontSize: '16px' }}
            click={() => handleAttachmentDownload(voucher, attachment, source)}
          />
        </div>
      </div>
    );
  };

  const renderAttachments = (
    voucher: EvaluationMergedVoucher,
    attachments: Attachment[],
    source: AttachmentSource
  ) => {
    if (!attachments || attachments.length === 0) {
      return <span css={styles.emptyValue}>-</span>;
    }

    return (
      <div css={styles.filesList}>
        {attachments.map((attachment, index) =>
          renderAttachmentRow(voucher, attachment, index, source)
        )}
      </div>
    );
  };

  const renderUnitSumRequirement = (voucher: EvaluationMergedVoucher) => {
    const chosenUnitType = getUnitTypeLabel(voucher.unitType);

    return (
      <div css={styles.unitSumWrapper}>
        <div css={styles.unitSumLine}>
          <span css={styles.unitSumLabel}>
            {intl.formatMessage({ id: 'count-label' })}
          </span>

          <span css={styles.unitSumValue}>
            {voucher.unitCount !== undefined && voucher.unitCount !== null
              ? voucher.unitCount
              : intl.formatMessage({ id: 'not-set' })}
          </span>
        </div>

        <div css={styles.unitSumLine}>
          <span css={styles.unitSumLabel}>
            {intl.formatMessage({ id: 'type-label' })}
          </span>

          <span css={styles.unitSumValue}>
            {chosenUnitType || intl.formatMessage({ id: 'not-set' })}
          </span>
        </div>
      </div>
    );
  };

  const renderTermsResponse = (voucher: EvaluationMergedVoucher) => {
    const response = voucher.response;

    if (response?.is_approved === true) {
      return (
        <Tag color="green" css={styles.responseTag}>
          {intl.formatMessage({ id: 'agreed' })}
        </Tag>
      );
    }

    if (response?.is_approved === false) {
      return (
        <Tag color="red" css={styles.responseTag}>
          {intl.formatMessage({ id: 'not-agreed' })}
        </Tag>
      );
    }

    return (
      <span css={styles.emptyValue}>
        {intl.formatMessage({ id: 'no-response-yet' })}
      </span>
    );
  };

  const renderRequirementContent = (voucher: EvaluationMergedVoucher) => {
    switch (getVoucherType(voucher)) {
      case 1:
        return renderUnitSumRequirement(voucher);

      case 2:
      case 4:
        return renderAttachments(
          voucher,
          voucher.projectAttachments as Attachment[],
          'project'
        );

      case 3:
        return <span css={styles.emptyValue}>-</span>;

      default:
        return renderAttachments(
          voucher,
          voucher.projectAttachments as Attachment[],
          'project'
        );
    }
  };

  const renderResponseContent = (voucher: EvaluationMergedVoucher) => {
    const response = voucher.response;

    switch (getVoucherType(voucher)) {
      case 1:
        return response?.sum !== null && response?.sum !== undefined ? (
          <span>
            {intl.formatMessage({
              id: 'evaluation-expanded-sum-label',
            })}
            : <b>{response.sum}</b>
          </span>
        ) : (
          <span css={styles.emptyValue}>
            {intl.formatMessage({
              id: 'evaluation-expanded-no-sum-delivered',
            })}
          </span>
        );

      case 2:
      case 3:
        return renderAttachments(
          voucher,
          voucher.vendorAttachments as Attachment[],
          'vendor'
        );

      case 4:
        return renderTermsResponse(voucher);

      default:
        return voucher.isDelivered ? (
          <span>
            {intl.formatMessage({
              id: 'evaluation-expanded-delivered',
            })}
          </span>
        ) : (
          <span css={styles.emptyValue}>
            {intl.formatMessage({
              id: 'evaluation-expanded-missing',
            })}
          </span>
        );
    }
  };

  const getRequirementLabel = (voucher: EvaluationMergedVoucher) => {
    if (getVoucherType(voucher) === 1) {
      return intl.formatMessage({ id: 'project-requirement' });
    }

    return intl.formatMessage({
      id: 'evaluation-expanded-project-files-label',
    });
  };

  const getResponseLabel = (voucher: EvaluationMergedVoucher) => {
    if (getVoucherType(voucher) === 2 || getVoucherType(voucher) === 3) {
      return intl.formatMessage({ id: 'vendor-file' });
    }

    return intl.formatMessage({
      id: 'evaluation-expanded-vendor-response-label',
    });
  };

  if (!visibleVouchers || visibleVouchers.length === 0) {
    return (
      <div css={styles.emptyWrapper}>
        <Empty
          description={intl.formatMessage({
            id: 'evaluation-expanded-empty-vouchers',
          })}
        />
      </div>
    );
  }

  return (
    <div css={styles.container}>
      <div css={styles.header}>
        <div css={styles.headerCell}>
          {intl.formatMessage({
            id: 'evaluation-expanded-header-voucher',
          })}
        </div>
        <div css={styles.headerCell}>
          {intl.formatMessage({
            id: 'evaluation-expanded-header-requirement',
          })}
        </div>
        <div css={styles.headerCell}>
          {intl.formatMessage({
            id: 'evaluation-expanded-header-response',
          })}
        </div>
        <div css={styles.headerCell}>
          {intl.formatMessage({
            id: 'evaluation-expanded-header-status',
          })}
        </div>
      </div>

      {visibleVouchers.map((voucher) => (
        <div key={voucher.id} css={styles.row}>
          <div css={styles.voucherCell}>
            <div css={styles.voucherTitleRow}>
              {voucher.isOptional && (
                <div css={styles.optionalBadgeSlot}>
                  {renderOptionalVoucherBadge(intl)}
                </div>
              )}

              <div css={styles.voucherTitle}>{voucher.name}</div>

              <div css={styles.voucherIcon}>
                {renderVoucherTypeIcon(Number(voucher.voucherType), intl)}
              </div>
            </div>

            {voucher.description && (
              <div css={styles.voucherDescription}>{voucher.description}</div>
            )}
          </div>

          <div css={styles.requirementCell}>
            <div css={styles.cellLabel}>{getRequirementLabel(voucher)}</div>
            {renderRequirementContent(voucher)}
          </div>

          <div css={styles.responseCell}>
            <div css={styles.cellLabel}>{getResponseLabel(voucher)}</div>
            <div css={styles.responsePreview}>
              {renderResponseContent(voucher)}
            </div>
          </div>

          <div css={styles.statusCell}>
            {voucher.isDelivered ? (
              <Tag color="green" css={styles.statusTag}>
                <CheckCircleOutlined />{' '}
                {intl.formatMessage({
                  id: 'evaluation-expanded-delivered',
                })}
              </Tag>
            ) : (
              <Tag color="red" css={styles.statusTag}>
                <CloseCircleOutlined />{' '}
                {intl.formatMessage({
                  id: 'evaluation-expanded-missing',
                })}
              </Tag>
            )}
          </div>
        </div>
      ))}

      {previewFile && (
        <Viewer
          mode="drawer"
          drawerVisible={previewDrawerVisible}
          onDrawerClose={() => {
            setPreviewDrawerVisible(false);
            setPreviewFile(null);
          }}
          drawerTitle={previewFile.name}
          drawerWidth={800}
          fileUrl={previewFile.url}
          fileExtension={previewFile.extension}
          showDownloadButton={true}
          onDownload={() =>
            handleAttachmentDownload(
              previewFile.voucher,
              previewFile.attachment,
              previewFile.source
            )
          }
          isLoadingUrl={isLoadingUrl}
        />
      )}
    </div>
  );
};

const styleFn: ThemedStyleFn = () => ({
  container: {
    border: '1px solid #e8e8e8',
    borderRadius: 6,
    background: '#ffffff',
    overflow: 'hidden',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
  },

  emptyWrapper: {
    padding: '28px 0',
    background: '#ffffff',
    border: '1px solid #e8e8e8',
    borderRadius: 6,
  },

  header: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1fr) 160px',
    gap: 12,
    padding: '10px 14px',
    background: '#fafafa',
    borderBottom: '1px solid #e8e8e8',
  },

  headerCell: {
    fontSize: 11,
    fontWeight: 700,
    color: '#595959',
    textTransform: 'uppercase',
    letterSpacing: 0.35,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  row: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1fr) 160px',
    gap: 12,
    padding: '14px',
    borderBottom: '1px solid #f0f0f0',
    alignItems: 'flex-start',
    minWidth: 0,
    maxWidth: '100%',

    '&:last-child': {
      borderBottom: 'none',
    },
  },

  voucherCell: {
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
  },

  voucherTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
  },

  optionalBadgeSlot: {
    flexShrink: 0,
  },

  voucherTitle: {
    fontWeight: 700,
    color: '#222222',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  voucherIcon: {
    flexShrink: 0,
  },

  voucherDescription: {
    marginTop: 6,
    fontSize: 12,
    color: '#777777',
    lineHeight: 1.4,
    maxWidth: '100%',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },

  requirementCell: {
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },

  responseCell: {
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },

  responsePreview: {
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },

  statusCell: {
    display: 'flex',
    alignItems: 'flex-start',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
  },

  cellLabel: {
    fontSize: 11,
    color: '#8c8c8c',
    marginBottom: 4,
  },

  emptyValue: {
    color: '#bfbfbf',
    fontSize: 12,
  },

  filesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    minWidth: 0,
    maxWidth: '100%',
  },

  fileItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    minWidth: 0,
    maxWidth: '100%',
    padding: '4px 6px',
    border: '1px solid #f0f0f0',
    borderRadius: 4,
    background: '#ffffff',
    boxSizing: 'border-box',
  },

  fileItemLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
    overflow: 'hidden',
  },

  fileIconContainer: {
    height: '18px',
    margin: 0,
  },

  fileName: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: 12,
  },

  unitSumWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    fontSize: 12,
    minWidth: 0,
    maxWidth: '100%',
  },

  unitSumLine: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 8,
    minWidth: 0,
  },

  unitSumLabel: {
    color: '#8c8c8c',
    flexShrink: 0,
  },

  unitSumValue: {
    fontWeight: 700,
    color: '#222222',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  statusTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    margin: 0,
    maxWidth: '100%',
  },

  responseTag: {
    margin: 0,
  },
});
